import { Flex, HStack, Text } from '@chakra-ui/react'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { useModalState } from '~/atoms/ModalState'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { toaster, ToastType } from '~/components/ui/toaster'
import { useGetExtensions } from '~/queries/extensionQueries'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { Spacing } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
  useMultiStyleConfig,
} from '~/utils/styleHelpers'

import { ExtensionInstance } from '@mangovoice/types'

const MoveFaxModalParts = [
  'footerContainer',
  'phoneNameText',
  'phoneNumberText',
] as const

const helpers = createMultiStyleConfigHelpers(MoveFaxModalParts)
export const MoveFaxModalStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      alignItems: 'stretch',
      gap: Spacing._2,
    },
    toInput: {
      height: '3.5rem',
    },
    phoneNameText: {
      fontWeight: '900',
      noOfLines: 1,
    },
    phoneNumberText: {
      flexShrink: 0,
    },

    footerContainer: {
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '0.62rem',
    },
  },
})

export const useMoveFaxModalStyle = (props?: StyleThemingProps) => {
  return useMultiStyleConfig(MoveFaxModalStyle, props) as MultiStyleType<
    typeof MoveFaxModalParts
  >
}

export const MoveFaxModal = () => {
  const { t } = useTranslation()
  const {
    moveFaxModal: [moveFaxModalState, setMoveFaxModalState],
  } = useModalState()
  const [selectedExtensionID, setSelectedExtensionID] = useState(0)
  const api = useMangoApiClient()

  const { results: VirtualFaxMachines, fetchNextPage } = useGetExtensions({
    type: 'VirtualFaxMachine',
  })
  const location = useLocation()
  const navigate = useNavigate()
  const faxId = location.pathname.split('/')[2]

  const styles = useMoveFaxModalStyle()

  const faxesJsx = useCallback((faxMachines: ExtensionInstance[]) => {
    return (
      faxMachines?.map((extension) => {
        return {
          value: extension.id,
          content: (
            <HStack key={extension.id} p="0.5rem">
              <Text css={styles.phoneNameText}>{extension.name}</Text>
              <Text css={styles.phoneNumberText}>{`${extension.number}`}</Text>
            </HStack>
          ),
        }
      }) ?? []
    )
  }, [])

  const selectedExtensionIndex = useMemo(() => {
    return VirtualFaxMachines?.findIndex(
      (fax) => fax.id === selectedExtensionID,
    )
  }, [VirtualFaxMachines, selectedExtensionID])

  function closeModal() {
    setMoveFaxModalState({ open: false })
  }

  async function moveFax() {
    if (!faxId || !selectedExtensionID) {
      return
    }

    const [moveFax, markFax] = await Promise.all([
      api.faxes.moveFaxToVirtualFax(Number(faxId), selectedExtensionID),
      api.faxes.markFaxAsUnread(Number(faxId)),
    ])

    if (moveFax.ok && markFax.ok) {
      closeModal()

      if (location.pathname.includes(ApplicationRoutes.FAX)) {
        // set query params to empty string
        navigate(ApplicationRoutes.FAX) // Resets the page in the "No Fax Selected" state
      }

      return
    }

    const errorMessages = [
      t(
        'moveFaxModal.toast.errorMoving',
        'An error occurred while moving the fax.',
      ),
    ]
    const type: ToastType = !moveFax.ok ? 'error' : 'warning'

    if (!markFax.ok) {
      errorMessages.push(
        t(
          'moveFaxModal.toast.notMarkedUnread',
          'Fax was not marked as unread.',
        ),
      )
    }

    if (!moveFax.ok) {
      errorMessages.push(
        t(
          'moveFaxModal.toast.moveFailed',
          'Failed to move the fax to the selected virtual fax.',
        ),
      )
    }

    toaster.create({
      title: t('moveFaxModal.toast.errorTitle', 'Error Moving Fax'),
      description: errorMessages.join(' '),
      type,
      duration: 5000,
      meta: { closable: true },
    })
  }

  return (
    <MangoModal
      open={moveFaxModalState.open}
      onOpenChange={(details) => {
        if (!details.open) closeModal()
      }}
      header={t('voicemail.tooltip.moveFax')}
      body={
        <Flex flexDirection="column" gap="3">
          <DropdownSelect
            placeholder={t('form.placeholder.selectVirtualFax')}
            items={faxesJsx(VirtualFaxMachines)}
            selectedIndex={selectedExtensionIndex}
            onScrollEndReached={fetchNextPage}
            onSelect={setSelectedExtensionID}
          />

          <Flex p="2">
            <Text>
              {t(
                'moveFaxModal.noAccessWarning',
                "If you don't have access to the selected Virtual Fax you won't be able to view this fax once it's been moved.",
              )}
            </Text>
          </Flex>
        </Flex>
      }
      footer={
        <Flex css={styles.footerContainer}>
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={closeModal}
          >
            {t('voicemail.mangoModal.cancelButtonText')}
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Default_300}
            onClick={moveFax}
          >
            {t('voicemail.tooltip.moveFax')}
          </MangoButton>
        </Flex>
      }
    ></MangoModal>
  )
}
